import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'

import { Avatar } from '@/components/Chips'
import { Icon, type IconName } from '@/components/Icon'
import {
  fetchNotifications,
  markAllRead,
  markRead,
  type AppNotification,
} from '@/features/notifications/api'
import type { ApiError } from '@/lib/api/client'

const ICON: Record<string, IconName> = {
  mentioned: 'comment',
  assigned: 'inbox',
  commented: 'comment',
  sla_breached: 'clock',
}

/**
 * What has been addressed to you, newest first.
 *
 * Each row carries the words as they were when it was written rather than
 * re-reading the record, so a task that has since been renamed — or deleted —
 * still reads as the thing you were told about.
 *
 * Used twice: in the panel behind the bell, and inline on the home page. The
 * same list either way, because two implementations of an inbox is two places
 * for the unread count to disagree.
 */
export function NotificationList({
  unreadOnly = false,
  limit,
  onNavigate,
}: {
  unreadOnly?: boolean
  limit?: number | undefined
  onNavigate?: (() => void) | undefined
}) {
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()

  const notifications = useQuery<AppNotification[], ApiError>({
    queryKey: ['notifications', { unread: unreadOnly }],
    queryFn: () => fetchNotifications(unreadOnly),
    retry: false,
  })

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['notifications'] })
    void queryClient.invalidateQueries({ queryKey: ['notifications-count'] })
  }

  const read = useMutation<void, ApiError, string>({
    mutationFn: (id) => markRead(id),
    onSuccess: refresh,
  })

  const readAll = useMutation<void, ApiError, void>({
    mutationFn: () => markAllRead(),
    onSuccess: refresh,
  })

  if (notifications.data === undefined) {
    return <p className="text-ink-500 px-4 py-3 font-mono text-[11px]">{t('common.loading')}</p>
  }

  if (notifications.data.length === 0) {
    return (
      <p className="text-ink-500 px-4 py-6 text-center text-[13px]">
        {unreadOnly ? t('notifications.emptyUnread') : t('notifications.empty')}
      </p>
    )
  }

  const rows = limit === undefined ? notifications.data : notifications.data.slice(0, limit)
  const anyUnread = notifications.data.some((row) => row.readAt === null)

  return (
    <div className="flex flex-col">
      {anyUnread ? (
        <div className="border-line flex items-center border-b px-4 py-1.5">
          <button
            type="button"
            onClick={() => readAll.mutate()}
            disabled={readAll.isPending}
            className="text-ink-500 hover:text-ink-900 ms-auto text-[11px] disabled:opacity-40"
          >
            {t('notifications.markAll')}
          </button>
        </div>
      ) : null}

      <ul className="divide-line divide-y">
        {rows.map((row) => (
          <li key={row.id} className={row.readAt === null ? 'bg-brand-50/50' : undefined}>
            <Row
              notification={row}
              locale={i18n.language}
              onOpen={() => {
                if (row.readAt === null) read.mutate(row.id)
                onNavigate?.()
              }}
            />
          </li>
        ))}
      </ul>
    </div>
  )
}

function Row({
  notification,
  locale,
  onOpen,
}: {
  notification: AppNotification
  locale: string
  onOpen: () => void
}) {
  const { t } = useTranslation()

  const verb = t(`notifications.kind.${notification.kind}`, { defaultValue: notification.kind })
  const label = notification.payload.key ?? notification.payload.title ?? ''

  // Where the row goes. A notification about a comment is about the item the
  // comment is on, because that is where the conversation can be read.
  const to = notification.projectId === null ? null : undefined

  const body = (
    <span className="flex gap-2.5 px-4 py-2.5 text-start">
      <span className="relative shrink-0">
        <Avatar name={notification.actor?.name ?? null} color={notification.actor?.color} size="sm" />
        <Icon
          name={ICON[notification.kind] ?? 'dot'}
          className="border-surface bg-surface-2 text-ink-500 absolute -bottom-1 -end-1 size-3.5 rounded-full border p-px"
        />
      </span>

      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-baseline gap-x-1.5">
          <span className="text-ink-900 text-[12px] font-medium">
            {notification.actor?.name ?? t('notifications.someone')}
          </span>
          <span className="text-ink-700 text-[12px]">{verb}</span>
          {label === '' ? null : (
            <span className="text-brand-600 font-mono text-[11px]">{label}</span>
          )}
          <time
            dateTime={notification.createdAt}
            className="text-ink-500 ms-auto shrink-0 font-mono text-[10px]"
          >
            {new Date(notification.createdAt).toLocaleDateString(locale)}
          </time>
        </span>

        {notification.payload.excerpt === undefined ? null : (
          <span className="text-ink-500 mt-0.5 block truncate text-[12px]">
            {notification.payload.excerpt}
          </span>
        )}
      </span>
    </span>
  )

  if (to === null) {
    return (
      <button type="button" onClick={onOpen} className="hover:bg-surface-2 block w-full">
        {body}
      </button>
    )
  }

  return (
    <Link to={`/projects/${notification.payload.key?.split('-').slice(0, 2).join('-') ?? ''}`} onClick={onOpen} className="hover:bg-surface-2 block">
      {body}
    </Link>
  )
}
